import { BtnPrimary, BtnCopy, BtnSecondary } from '@components/buttons';
import { IcoPlus, IcoExternalLink, IcoTrash, IcoCheck } from '@components/icons';
import { RuzcalMgmtPage } from './mgmt-page';
import { PageContent, PageSection, defCalRoute } from './common';
import { LoadedProps } from '@components/router';
import { showDialog } from '@components/dialog';
import { InputField } from '@components/calendar-form';
import { Toggle } from '@components/toggle';
import { showToast } from '@components/toaster';
import { compactDate } from 'shared/dateutil/date-formats';
import { rpx } from 'client/lib/rpx-client';
import type { DomainRow } from 'server/types';
import { SettingsTabs, SectionHeading } from './settings-shared';

export const route = defCalRoute({ authLevel: 'guide', load, Page });

async function load() {
  const result = await rpx.domains.getAccountDomains();
  return result;
}

function Page({ state, setState }: LoadedProps<typeof load>) {
  const { domains, hasActiveAccount } = state;

  const handleAddDomain = () =>
    showDialog({
      mode: 'none',
      title: 'Add Custom Domain',
      children: <AddDomainForm hasExistingDomains={domains.length > 0} />,
      onSubmit: async (data: { domain: string; isDefault?: boolean }) => {
        const hasDefault = domains.some((d) => d.isDefault);
        const makeDefault = data.isDefault || !hasDefault;

        const newDomain = await rpx.domains.addAccountDomain({
          domain: data.domain,
          isDefault: makeDefault,
        });

        setState((s) => {
          const prev = newDomain.isDefault
            ? s.domains.map((x) => ({ ...x, isDefault: false }))
            : s.domains;
          return { ...s, domains: [...prev, newDomain] };
        });

        showToast({ title: 'Success', message: 'Domain added successfully', type: 'ok' });
        return true;
      },
    });

  const handleSetDefault = async (domainId: string) => {
    try {
      await rpx.domains.setDefaultAccountDomain({ domainId });
      setState((s) => ({
        ...s,
        domains: s.domains.map((d) => ({
          ...d,
          isDefault: d.id === domainId,
        })),
      }));
      showToast({ title: 'Success', message: 'Default domain updated', type: 'ok' });
    } catch (error) {
      showToast({ title: 'Error', message: 'Failed to update default domain', type: 'warn' });
    }
  };

  const handleRemoveDomain = async (domain: DomainRow) => {
    const confirmed = await showDialog({
      mode: 'warn',
      title: 'Remove Domain',
      children: (
        <div class="flex flex-col gap-4">
          <p class="text-gray-600">
            Are you sure you want to remove <strong class="text-gray-900">{domain.domain}</strong>?
          </p>

          {domain.isDefault && (
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p class="text-sm text-yellow-800">
                <strong>Warning:</strong> This is currently your default domain. You'll need to set
                another domain as default after removing this one.
              </p>
            </div>
          )}

          <p class="text-sm text-gray-500">
            This action cannot be undone. Your landing pages will no longer be accessible on this
            domain.
          </p>
        </div>
      ),
      confirmButtonText: 'Remove Domain',
    });

    if (!confirmed) {
      return;
    }

    try {
      await rpx.domains.removeAccountDomain({ domainId: domain.id });
      setState((s) => ({ ...s, domains: s.domains.filter((d) => d.id !== domain.id) }));
      showToast({ title: 'Success', message: 'Domain removed successfully', type: 'ok' });
    } catch (error) {
      showToast({ title: 'Error', message: 'Failed to remove domain', type: 'warn' });
    }
  };

  return (
    <RuzcalMgmtPage title="Custom Domains" currentPage="settings">
      <PageContent>
        <PageSection>
          <SettingsTabs currentTab="domains" />

          <div class="flex flex-col gap-8 w-lg">
            <div>
              <SectionHeading>Connected Domains</SectionHeading>
              <p class="text-gray-600 text-sm mt-2">
                Connect your own domain to host your landing pages and calendar booking pages.
              </p>
            </div>

            {!hasActiveAccount ? (
              <div class="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-2xl p-8">
                <div class="text-center py-8">
                  <div class="w-20 h-20 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <IcoExternalLink class="size-10 text-white" />
                  </div>
                  <h3 class="text-2xl font-bold text-gray-900 mb-3">Unlock Custom Domains</h3>
                  <p class="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                    Take your brand to the next level with custom domains. Create professional,
                    branded URLs for your landing pages that build trust and recognition.
                  </p>
                  <button class="w-full max-w-sm mx-auto bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white py-4 px-8 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
                    <a href="/calendar/signup" class="block text-white no-underline">
                      Get Started with Pro
                    </a>
                  </button>
                </div>
              </div>
            ) : domains.length === 0 ? (
              <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div class="text-center py-8">
                  <div class="text-gray-400 mb-4">
                    <IcoExternalLink class="size-12 mx-auto" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-2">No custom domains yet</h3>
                  <p class="text-gray-500 mb-6">
                    Add your first custom domain to get started with branded landing pages.
                  </p>
                  <BtnPrimary class="inline-flex items-center gap-2" onClick={handleAddDomain}>
                    <IcoPlus class="size-4" />
                    Add Custom Domain
                  </BtnPrimary>
                </div>
              </div>
            ) : (
              <div class="flex flex-col gap-4">
                {domains.map((domain) => (
                  <DomainCard
                    key={domain.id}
                    domain={domain}
                    onSetDefault={() => handleSetDefault(domain.id)}
                    onRemove={() => handleRemoveDomain(domain)}
                  />
                ))}
              </div>
            )}

            {hasActiveAccount && domains.length > 0 && (
              <BtnSecondary
                class="self-start inline-flex items-center gap-2"
                onClick={handleAddDomain}
              >
                <IcoPlus class="size-4" />
                Add Another Domain
              </BtnSecondary>
            )}

            {hasActiveAccount && (
              <>
                <div class="border-t border-gray-300 pt-4">
                  <SectionHeading>How it works</SectionHeading>
                </div>

                <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 class="font-medium text-blue-900 mb-3">Setting up your custom domain</h4>
                  <ol class="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li>Add your domain (e.g., book.example.com)</li>
                    <li>
                      <span>Update your DNS settings with a CNAME record pointing to:</span>
                      <BtnCopy
                        value="courses.ruzuku.com"
                        class="bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded text-blue-900 font-mono text-xs ml-2"
                      >
                        courses.ruzuku.com
                      </BtnCopy>
                    </li>
                    <li>Wait for verification (usually takes a few minutes)</li>
                    <li>Your landing pages will be available on your custom domain</li>
                  </ol>
                </div>

                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p class="text-sm text-yellow-800">
                    <strong>Note:</strong> Landing pages will only be served on custom domains, not
                    on {location.hostname}.
                  </p>
                </div>
              </>
            )}
          </div>
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}

function DomainCard({
  domain,
  onSetDefault,
  onRemove,
}: {
  domain: DomainRow;
  onSetDefault: () => void;
  onRemove: () => void;
}) {
  return (
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center gap-2">
            <h4 class="font-medium text-gray-900">{domain.domain}</h4>
            {domain.isDefault && (
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Default
              </span>
            )}
          </div>
          <div class="flex items-center gap-4 mt-1">
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              <IcoCheck class="size-3" />
              Active
            </span>
            <span class="text-sm text-gray-500">Added {compactDate(domain.createdAt)}</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          {!domain.isDefault && <BtnSecondary onClick={onSetDefault}>Set as Default</BtnSecondary>}
          <button
            onClick={onRemove}
            class="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Remove domain"
          >
            <IcoTrash class="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AddDomainForm({ hasExistingDomains }: { hasExistingDomains?: boolean }) {
  return (
    <section class="flex flex-col gap-4">
      <div>
        <p class="text-gray-600 text-sm mb-4">
          Enter the domain you want to use for your landing pages. We recommend using a subdomain
          like "book.example.com" or "calendar.example.com".
        </p>
      </div>

      <InputField
        name="domain"
        title="Domain"
        type="text"
        placeholder="book.example.com"
        required
        pattern="^[a-zA-Z0-9\-_.]+$"
        subtitle="Enter a valid domain name (e.g., book.example.com)"
        fullWidth
        autoFocus
      />

      {hasExistingDomains && (
        <div class="border border-gray-200 rounded-lg p-4">
          <label class="flex items-center gap-3">
            <Toggle name="isDefault" />
            <div>
              <span class="font-medium text-gray-900">Set as default domain</span>
              <p class="text-sm text-gray-500">
                Use this domain as the default for new landing pages
              </p>
            </div>
          </label>
        </div>
      )}

      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p class="text-sm text-blue-800">
          <strong>Next steps:</strong> After adding your domain, you'll need to update your DNS
          settings with a CNAME record pointing to{' '}
          <code class="font-mono bg-blue-100 px-1 rounded">courses.ruzuku.com</code>
        </p>
      </div>
    </section>
  );
}
